import React, { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Briefcase, Upload, CheckCircle } from 'lucide-react';
import { jobBoardApi } from '../../api/jobBoardApi';
import ReCAPTCHA from 'react-google-recaptcha';
import RecaptchaCheckbox from '../../components/RecaptchaCheckbox';

const ApplyForm = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [file] = useState<File | null>(null);
  const [formData] = useState({
    fullName: '',
    emailAddress: '',
    contactNumber: '',
  });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (!jobId) return;
    if (!captchaToken) {
      setCaptchaError('Please complete the CAPTCHA verification.');
      return;
    }

    setLoading(true);
    const data = new FormData();
    data.append('JobId', jobId);
    data.append('FullName', formData.fullName);
    data.append('EmailAddress', formData.emailAddress);
    data.append('ContactNumber', formData.contactNumber);
    if (file) data.append('resume', file);
    data.append('captchaToken', captchaToken);

    try {
      await jobBoardApi.applyForJob(data);
      setSubmitted(true);
    } catch (err) {
      console.error("Application failed:", err);
      const response = (err as { response?: { data?: { code?: string; message?: string } } })?.response;
      alert(response?.data?.code === 'CAPTCHA_FAILED' ? 'Please complete the CAPTCHA verification.' : response?.data?.message || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-12 rounded-[3rem] shadow-2xl text-center max-w-md border border-slate-100">
          <CheckCircle className="text-emerald-500 mx-auto mb-6" size={80} />
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Success!</h1>
          <p className="text-slate-500 mb-8 leading-relaxed">Your application has been received. Our team will review your CV and get back to you shortly.</p>
          <button onClick={() => navigate('/')} className="w-full py-4 bg-strivre-burgundy text-white rounded-2xl font-bold shadow-lg shadow-red-200 hover:bg-red-900 transition-all">Return to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 lg:p-12 rounded-[2.5rem] shadow-2xl border border-slate-100 w-full max-w-2xl">
        <header className="mb-6 text-center">
          <div className="w-16 h-16 bg-strivre-burgundy rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-red-200">
            <Briefcase className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Submit Application</h1>
          <p className="text-slate-500 font-medium">Position Ref: <span className="text-indigo-600">{jobId}</span></p>
        </header>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
            <input type="text" className="w-full p-3.5 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. Alexander Graham" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
            <input type="email" className="w-full p-3.5 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="alex@example.com" required />
          </div>
          
          <div className="relative border-2 border-dashed border-strivre-lightnavy rounded-3xl p-8 text-center hover:border-indigo-400 transition-colors cursor-pointer group">
            <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept=".pdf,.doc,.docx" required />
            <Upload className="mx-auto text-slate-300 group-hover:text-indigo-500 transition-colors mb-2" size={32} />
            <p className="text-sm font-bold text-slate-600">Drag and drop your CV here</p>
            <p className="text-xs text-slate-400 mt-1">PDF or Word (max. 5MB)</p>
          </div>

          <div>
            <RecaptchaCheckbox ref={recaptchaRef} onChange={(token) => { setCaptchaToken(token); setCaptchaError(''); }} disabled={loading} />
            {captchaError && <p className="mt-2 text-sm text-red-600">{captchaError}</p>}
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-strivre-burgundy text-white py-4 rounded-2xl font-bold hover:bg-red-900 transition-all shadow-lg shadow-red-200 active:scale-95"
          >
            Submit My Application
          </button>
        </form>
      </div>
    </div>
  );
};

export default ApplyForm;